import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './layout/Layout';

interface FlaggedUser {
  user_id: number;
  user: {
    id: number;
    name: string;
    email: string;
    account_number: string;
    profile_picture: string | null;
    is_banned: boolean;
  };
  total_reports: number;
  pending_reports: number;
  report_reasons: Record<string, number>;
  latest_report_date: string;
  reporters: Array<{
    id: number;
    name: string;
    profile_picture: string | null;
  }>;
}

interface Report {
  id: number;
  report_reason: string;
  report_description: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reporter: {
    id: number;
    name: string;
    email: string;
    profile_picture: string | null;
    account_number: string;
  };
  reportedUser?: {
    id: number;
    name: string;
    email: string;
    profile_picture: string | null;
    account_number: string;
  };
  reported_user_id?: number;
}

interface ReporterData {
  reporter: {
    id: number;
    name: string;
    email: string;
    profile_picture: string | null;
    account_number: string;
    created_at: string;
  };
  total_reports: number;
  status_breakdown: {
    pending: number;
    reviewed: number;
    resolved: number;
    dismissed: number;
  };
  reason_breakdown: Record<string, number>;
  reports: Report[];
}

interface Stats {
  total_reports: number;
  pending_reports: number;
  banned_reported_users: number;
  resolved_today: number;
  resolved_trend: number;
  reports_today: number;
  reports_trend: number;
}

const FlaggedUsers: React.FC = () => {
  const navigate = useNavigate();
  const [flaggedUsers, setFlaggedUsers] = useState<FlaggedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [filters, setFilters] = useState({
    reason: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  
  // Modal states
  const [selectedUser, setSelectedUser] = useState<FlaggedUser | null>(null);
  const [selectedReporter, setSelectedReporter] = useState<ReporterData | null>(null);
  const [userReports, setUserReports] = useState<Report[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showReporterModal, setShowReporterModal] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingReporter, setLoadingReporter] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Fetch flagged users data
  const fetchFlaggedUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post('/admin/flagged-users', {
        start: (pagination.page - 1) * pagination.limit,
        length: pagination.limit,
        search: { value: filters.search },
        order: [],
        columns: [],
        filters: filters
      });

      if (response.data.success) {
        setFlaggedUsers(response.data.data.flagged_users);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.recordsTotal,
          totalPages: Math.ceil(response.data.data.recordsTotal / prev.limit)
        }));
      }
    } catch (error) {
      console.error('Error fetching flagged users:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/flagged-users/stats');
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch user reports
  const fetchUserReports = async (userId: number) => {
    setLoadingReports(true);
    try {
      const response = await axios.get(`/admin/flagged-users/${userId}/reports`);
      if (response.data.success) {
        setUserReports(response.data.data.reports);
      }
    } catch (error) {
      console.error('Error fetching user reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  // Fetch reporter details
  const fetchReporterDetails = async (reporterId: number) => {
    setLoadingReporter(true);
    try {
      const response = await axios.get(`/admin/reporters/${reporterId}/details`);
      if (response.data.success) {
        setSelectedReporter(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching reporter details:', error);
    } finally {
      setLoadingReporter(false);
    }
  };

  // Open user details modal
  const openUserModal = async (user: FlaggedUser) => {
    setSelectedUser(user);
    setShowUserModal(true);
    await fetchUserReports(user.user_id);
  };

  // Open reporter details modal
  const openReporterModal = async (reporterId: number) => {
    setShowReporterModal(true);
    await fetchReporterDetails(reporterId);
  };

  // Close modals
  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setUserReports([]);
  };

  const closeReporterModal = () => {
    setShowReporterModal(false);
    setSelectedReporter(null);
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchFlaggedUsers();
  };

  // User selection handlers
  const handleSelectUser = (userId: number) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === flaggedUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(flaggedUsers.map(user => user.user_id));
    }
  };

  // Send email handlers
  const handleSendEmailToSelected = () => {
    if (selectedUserIds.length === 0) return;
    navigate('/communication/send-email', { 
      state: { userIds: selectedUserIds } 
    });
  };

  const handleSendEmailToUser = (userId: number) => {
    navigate('/communication/send-email', { 
      state: { userIds: [userId] } 
    });
  };

  // Send push handlers
  const handleSendPushToSelected = () => {
    if (selectedUserIds.length === 0) return;
    navigate('/communication/send-push', { 
      state: { userIds: selectedUserIds } 
    });
  };

  const handleSendPushToUser = (userId: number) => {
    navigate('/communication/send-push', { 
      state: { userIds: [userId] } 
    });
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      reason: 'all',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Ban user functionality
  const banUser = async (userId: number, userName: string) => {
    try {
      // Show ban duration selection
      const duration = window.prompt(
        `Ban ${userName} for how long?\n\nOptions:\n- 24 (24 hours)\n- 168 (7 days)\n- 720 (30 days)\n- permanent\n\nEnter duration in hours or 'permanent':`,
        '168'
      );

      if (!duration) return;

      // Parse duration
      let banDuration = 'permanent';
      if (duration.trim().toLowerCase() !== 'permanent') {
        const hours = parseInt(duration.trim());
        if (!isNaN(hours) && hours > 0) {
          banDuration = hours.toString();
        }
      }

      const response = await axios.post('/admin/users/ban', {
        userId: userId,
        banDuration: banDuration
      });

      if (response.data.success) {
        alert(`${userName} has been banned successfully!`);
        
        // Refresh the flagged users list
        await fetchFlaggedUsers();
        await fetchStats();
        
        // Update the selected user if it's the same user
        if (selectedUser && selectedUser.user.id === userId) {
          setSelectedUser(prev => prev ? {
            ...prev,
            user: { ...prev.user, is_banned: true }
          } : null);
        }
        
        // Update the flagged users list to reflect the ban status
        setFlaggedUsers(prev => prev.map(user => 
          user.user.id === userId 
            ? { ...user, user: { ...user.user, is_banned: true } }
            : user
        ));
      } else {
        alert(`Failed to ban ${userName}: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Error banning user:', error);
      const errorMessage = error.response?.data?.message || 'Failed to ban user';
      alert(`Error: ${errorMessage}`);
    }
  };

  // Remove ban functionality
  const removeBan = async (userId: number, userName: string) => {
    try {
      if (!window.confirm(`Are you sure you want to remove the ban from ${userName}?`)) {
        return;
      }

      const response = await axios.delete(`/admin/users/${userId}/ban`);

      if (response.data.success) {
        alert(`${userName}'s ban has been removed successfully!`);
        
        // Refresh the flagged users list
        await fetchFlaggedUsers();
        await fetchStats();
        
        // Update the selected user if it's the same user
        if (selectedUser && selectedUser.user.id === userId) {
          setSelectedUser(prev => prev ? {
            ...prev,
            user: { ...prev.user, is_banned: false }
          } : null);
        }
        
        // Update the flagged users list to reflect the unban status
        setFlaggedUsers(prev => prev.map(user => 
          user.user.id === userId 
            ? { ...user, user: { ...user.user, is_banned: false } }
            : user
        ));
      } else {
        alert(`Failed to remove ban from ${userName}: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Error removing ban:', error);
      const errorMessage = error.response?.data?.message || 'Failed to remove ban';
      alert(`Error: ${errorMessage}`);
    }
  };

  // Delete report functionality
  const deleteReport = async (reportId: number) => {
    try {
      if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
        return;
      }

      const response = await axios.delete(`/admin/reports/${reportId}`);

      if (response.data.success) {
        alert('Report deleted successfully!');
        
        // Refresh the flagged users list and stats
        await fetchFlaggedUsers();
        await fetchStats();
        
        // Refresh user report details if modal is open
        if (selectedUser) {
          await fetchUserReports(selectedUser.user_id);
        }
      } else {
        alert(`Failed to delete report: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error('Error deleting report:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete report';
      alert(`Error: ${errorMessage}`);
    }
  };

  // Load data on component mount and when filters/pagination change
  useEffect(() => {
    fetchFlaggedUsers();
    fetchStats();
  }, [fetchFlaggedUsers]);

  // Get report reason color
  const getReasonColor = (reason: string) => {
    const colors: Record<string, string> = {
      spam: 'bg-warning',
      inappropriate: 'bg-danger',
      fake: 'bg-info',
      harassment: 'bg-danger',
      underage: 'bg-warning',
      violence: 'bg-danger',
      other: 'bg-secondary'
    };
    return colors[reason] || 'bg-secondary';
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-warning',
      reviewed: 'bg-info',
      resolved: 'bg-success',
      dismissed: 'bg-secondary'
    };
    return colors[status] || 'bg-secondary';
  };

  return (
    <Layout>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Flagged Users</h4>
              <p className="text-muted mb-0">Review and manage flagged user reports</p>
            </div>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item">
                  <a href="/dashboard" style={{ color: '#006175' }}>Nyvro</a>
                </li>
                <li className="breadcrumb-item active">Flagged Users</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-xl-4 col-md-6 mb-3">
          <div className="card stats-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <p className="text-muted mb-1 fw-medium">Total Reports</p>
                  <h3 className="mb-0 fw-bold" style={{ color: '#ffc107' }}>
                    {stats?.total_reports || 0}
                  </h3>
                  <small className={stats?.reports_trend && stats.reports_trend >= 0 ? 'text-success' : 'text-danger'}>
                    <i className={`bx ${stats?.reports_trend && stats.reports_trend >= 0 ? 'bx-trending-up' : 'bx-trending-down'} me-1`}></i>
                    {stats?.reports_trend && stats.reports_trend >= 0 ? '+' : ''}{stats?.reports_trend || 0} from yesterday
                  </small>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                       style={{ backgroundColor: '#ffc10720' }}>
                    <i className="bx bx-flag font-size-24" style={{ color: '#ffc107' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6 mb-3">
          <div className="card stats-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <p className="text-muted mb-1 fw-medium">Banned Reported Users</p>
                  <h3 className="mb-0 fw-bold" style={{ color: '#dc3545' }}>
                    {stats?.banned_reported_users || 0}
                  </h3>
                  <small className="text-danger">
                    <i className="bx bx-block me-1"></i>
                    Users banned due to reports
                  </small>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                       style={{ backgroundColor: '#dc354520' }}>
                    <i className="bx bx-block font-size-24" style={{ color: '#dc3545' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6 mb-3">
          <div className="card stats-card h-100">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <p className="text-muted mb-1 fw-medium">Pending</p>
                  <h3 className="mb-0 fw-bold" style={{ color: '#fd7e14' }}>
                    {stats?.pending_reports || 0}
                  </h3>
                  <small className="text-warning">
                    <i className="bx bx-time me-1"></i>
                    Requires attention
                  </small>
                </div>
                <div className="flex-shrink-0">
                  <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                       style={{ backgroundColor: '#fd7e1420' }}>
                    <i className="bx bx-time font-size-24" style={{ color: '#fd7e14' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Toolbar */}
      {selectedUserIds.length > 0 && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-primary d-flex justify-content-between align-items-center">
              <span>
                <i className="bx bx-check-circle me-2"></i>
                {selectedUserIds.length} user(s) selected
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSendEmailToSelected}
                >
                  <i className="bx bx-envelope me-1"></i>
                  Send Email
                </button>
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleSendPushToSelected}
                >
                  <i className="bx bx-bell me-1"></i>
                  Send Push
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bx bx-filter me-2"></i>
                Filters
              </h5>
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Reason</label>
                  <select 
                    className="form-select"
                    value={filters.reason}
                    onChange={(e) => handleFilterChange('reason', e.target.value)}
                  >
                    <option value="all">All Reasons</option>
                    <option value="spam">Spam</option>
                    <option value="inappropriate">Inappropriate</option>
                    <option value="fake">Fake</option>
                    <option value="harassment">Harassment</option>
                    <option value="underage">Underage</option>
                    <option value="violence">Violence</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">From Date</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">To Date</label>
                  <input 
                    type="date" 
                    className="form-control"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Search</label>
                  <input 
                    type="text" 
                    className="form-control"
                    placeholder="User name, email..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-12">
                  <button 
                    className="btn btn-primary me-2"
                    onClick={applyFilters}
                  >
                    <i className="bx bx-search me-1"></i>
                    Apply Filters
                  </button>
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={resetFilters}
                  >
                    <i className="bx bx-reset me-1"></i>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-2">Loading flagged users...</p>
                </div>
              ) : flaggedUsers.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                    <i className="bx bx-flag font-size-48 text-muted"></i>
                  </div>
                  <h5 className="mb-3">No Flagged Users Found</h5>
                  <p className="text-muted">No users match your current filter criteria.</p>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '50px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedUserIds.length === flaggedUsers.length && flaggedUsers.length > 0}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>User</th>
                          <th>Total Reports</th>
                          <th>Reporters</th>
                          <th>Latest Report</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flaggedUsers.map((flaggedUser) => (
                          <tr key={flaggedUser.user_id} className={selectedUserIds.includes(flaggedUser.user_id) ? 'table-active' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectedUserIds.includes(flaggedUser.user_id)}
                                onChange={() => handleSelectUser(flaggedUser.user_id)}
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0 me-3">
                                  <img
                                    src={flaggedUser.user.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(flaggedUser.user.name)}&background=667eea&color=fff&size=40&bold=true`}
                                    className="rounded-circle"
                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                    alt={flaggedUser.user.name}
                                    onError={(e) => {
                                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(flaggedUser.user.name)}&background=667eea&color=fff&size=40&bold=true`;
                                    }}
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-0">{flaggedUser.user.name}</h6>
                                  <small className="text-muted">{flaggedUser.user.email}</small>
                                  <br />
                                  <small className="text-muted">#{flaggedUser.user.account_number}</small>
                                  {flaggedUser.user.is_banned && (
                                    <span className="badge bg-danger ms-2">Banned</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-primary fs-6">{flaggedUser.total_reports}</span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="d-flex">
                                  {flaggedUser.reporters.slice(0, 4).map((reporter, index) => (
                                    <div
                                      key={reporter.id}
                                      className="position-relative"
                                      style={{ marginLeft: index > 0 ? '-8px' : '0', cursor: 'pointer' }}
                                      onClick={() => openReporterModal(reporter.id)}
                                      title={reporter.name}
                                    >
                                      <img
                                        src={reporter.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(reporter.name)}&background=667eea&color=fff&size=32&bold=true`}
                                        className="rounded-circle border border-2 border-white"
                                        style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                        alt={reporter.name}
                                      />
                                    </div>
                                  ))}
                                </div>
                                {flaggedUser.reporters.length > 4 && (
                                  <small className="text-muted ms-2">
                                    +{flaggedUser.reporters.length - 4} more
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(flaggedUser.latest_report_date).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openUserModal(flaggedUser)}
                                  title="View Details"
                                >
                                  <i className="bx bx-show"></i>
                                </button>
                                {!flaggedUser.user.is_banned ? (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to ban ${flaggedUser.user.name}?`)) {
                                        banUser(flaggedUser.user.id, flaggedUser.user.name);
                                      }
                                    }}
                                    title="Ban User"
                                  >
                                    <i className="bx bx-block"></i>
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-sm btn-success"
                                    onClick={() => removeBan(flaggedUser.user.id, flaggedUser.user.name)}
                                    title="Remove Ban"
                                  >
                                    <i className="bx bx-check"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => handleSendEmailToUser(flaggedUser.user_id)}
                                  title="Send Email"
                                >
                                  <i className="bx bx-envelope"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => handleSendPushToUser(flaggedUser.user_id)}
                                  title="Send Push"
                                >
                                  <i className="bx bx-bell"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div className="text-muted">
                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                      </div>
                      <nav>
                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link"
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={pagination.page === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(pagination.totalPages)].map((_, index) => {
                            const page = index + 1;
                            if (page === 1 || page === pagination.totalPages || (page >= pagination.page - 2 && page <= pagination.page + 2)) {
                              return (
                                <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                                  <button 
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            } else if (page === pagination.page - 3 || page === pagination.page + 3) {
                              return <li key={page} className="page-item disabled"><span className="page-link">...</span></li>;
                            }
                            return null;
                          })}
                          <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                            <button 
                              className="page-link"
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={pagination.page === pagination.totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bx bx-user me-2"></i>
                  User Details: {selectedUser.user.name}
                </h5>
                <button type="button" className="btn-close" onClick={closeUserModal}></button>
              </div>
              <div className="modal-body">
                {/* User Profile Info */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <img
                        src={selectedUser.user.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.user.name)}&background=667eea&color=fff&size=80&bold=true`}
                        className="rounded-circle me-3"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                        alt={selectedUser.user.name}
                      />
                      <div>
                        <h5 className="mb-1">{selectedUser.user.name}</h5>
                        <p className="text-muted mb-1">{selectedUser.user.email}</p>
                        <p className="text-muted mb-0">#{selectedUser.user.account_number}</p>
                        {selectedUser.user.is_banned && (
                          <span className="badge bg-danger mt-1">Banned</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="row text-center">
                      <div className="col-4">
                        <div className="border rounded p-2">
                          <h4 className="text-primary mb-1">{selectedUser.total_reports}</h4>
                          <small className="text-muted">Total Reports</small>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="border rounded p-2">
                          <h4 className="text-warning mb-1">{selectedUser.pending_reports}</h4>
                          <small className="text-muted">Pending</small>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="border rounded p-2">
                          <h4 className="text-info mb-1">{selectedUser.reporters.length}</h4>
                          <small className="text-muted">Reporters</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reports Table */}
                <h6 className="mb-3">All Reports Against This User</h6>
                {loadingReports ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reporter</th>
                          <th>Reason</th>
                          <th>Description</th>
                          <th>Date</th>
                          <th>Admin Notes</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userReports.map((report) => (
                          <tr key={report.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={report.reporter.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(report.reporter.name)}&background=667eea&color=fff&size=32&bold=true`}
                                  className="rounded-circle me-2"
                                  style={{ width: '32px', height: '32px', objectFit: 'cover', cursor: 'pointer' }}
                                  alt={report.reporter.name}
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(report.reporter.name)}&background=667eea&color=fff&size=32&bold=true`;
                                  }}
                                  onClick={() => {
                                    closeUserModal();
                                    openReporterModal(report.reporter.id);
                                  }}
                                  title={`View ${report.reporter.name}'s details`}
                                />
                                <div>
                                  <div className="fw-semibold">{report.reporter.name}</div>
                                  <small className="text-muted">#{report.reporter.account_number}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${getReasonColor(report.report_reason)}`}>
                                {report.report_reason}
                              </span>
                            </td>
                            <td>
                              <div style={{ maxWidth: '200px' }}>
                                <p className="mb-0 text-truncate" title={report.report_description}>
                                  {report.report_description || 'No description'}
                                </p>
                              </div>
                            </td>
                            <td>
                              <small>{new Date(report.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <div style={{ maxWidth: '150px' }}>
                                <p className="mb-0 text-truncate" title={report.admin_notes || ''}>
                                  {report.admin_notes || 'No notes'}
                                </p>
                              </div>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteReport(report.id)}
                                title="Delete report"
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeUserModal}>
                  Close
                </button>
                {!selectedUser.user.is_banned ? (
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={() => {
                      if (window.confirm(`Are you sure you want to ban ${selectedUser.user.name}?`)) {
                        banUser(selectedUser.user.id, selectedUser.user.name);
                        closeUserModal();
                      }
                    }}
                  >
                    <i className="bx bx-block me-1"></i>
                    Ban User
                  </button>
                ) : (
                  <button 
                    type="button" 
                    className="btn btn-success"
                    onClick={() => {
                      removeBan(selectedUser.user.id, selectedUser.user.name);
                      closeUserModal();
                    }}
                  >
                    <i className="bx bx-check me-1"></i>
                    Remove Ban
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reporter Details Modal */}
      {showReporterModal && selectedReporter && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bx bx-user-check me-2"></i>
                  Reporter Details: {selectedReporter.reporter.name}
                </h5>
                <button type="button" className="btn-close" onClick={closeReporterModal}></button>
              </div>
              <div className="modal-body">
                {/* Reporter Profile Info */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center">
                      <img
                        src={selectedReporter.reporter.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedReporter.reporter.name)}&background=667eea&color=fff&size=80&bold=true`}
                        className="rounded-circle me-3"
                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                        alt={selectedReporter.reporter.name}
                      />
                      <div>
                        <h5 className="mb-1">{selectedReporter.reporter.name}</h5>
                        <p className="text-muted mb-1">{selectedReporter.reporter.email}</p>
                        <p className="text-muted mb-0">#{selectedReporter.reporter.account_number}</p>
                        <small className="text-muted">
                          Member since: {new Date(selectedReporter.reporter.created_at).toLocaleDateString()}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="row text-center">
                      <div className="col-3">
                        <div className="border rounded p-2">
                          <h4 className="text-primary mb-1">{selectedReporter.total_reports}</h4>
                          <small className="text-muted">Total Reports</small>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="border rounded p-2">
                          <h4 className="text-warning mb-1">{selectedReporter.status_breakdown.pending}</h4>
                          <small className="text-muted">Pending</small>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="border rounded p-2">
                          <h4 className="text-success mb-1">{selectedReporter.status_breakdown.resolved}</h4>
                          <small className="text-muted">Resolved</small>
                        </div>
                      </div>
                      <div className="col-3">
                        <div className="border rounded p-2">
                          <h4 className="text-secondary mb-1">{selectedReporter.status_breakdown.dismissed}</h4>
                          <small className="text-muted">Dismissed</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reports Made by This Reporter */}
                <h6 className="mb-3">Reports Made by This Reporter</h6>
                {loadingReporter ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Reported User</th>
                          <th>Reason</th>
                          <th>Description</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedReporter.reports.map((report) => (
                          <tr key={report.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <img
                                  src={report.reportedUser?.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(report.reportedUser?.name || 'Unknown')}&background=667eea&color=fff&size=32&bold=true`}
                                  className="rounded-circle me-2"
                                  style={{ width: '32px', height: '32px', objectFit: 'cover', cursor: 'pointer' }}
                                  alt={report.reportedUser?.name || 'Unknown User'}
                                  onClick={() => {
                                    closeReporterModal();
                                    // Find and open the reported user modal
                                    const reportedUser = flaggedUsers.find(fu => fu.user_id === report.reported_user_id);
                                    if (reportedUser) {
                                      openUserModal(reportedUser);
                                    }
                                  }}
                                  title={`View ${report.reportedUser?.name || 'Unknown User'}'s details`}
                                />
                                <div>
                                  <div className="fw-semibold">{report.reportedUser?.name || 'Unknown User'}</div>
                                  <small className="text-muted">#{report.reportedUser?.account_number || 'N/A'}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className={`badge ${getReasonColor(report.report_reason)}`}>
                                {report.report_reason}
                              </span>
                            </td>
                            <td>
                              <div style={{ maxWidth: '200px' }}>
                                <p className="mb-0 text-truncate" title={report.report_description}>
                                  {report.report_description || 'No description'}
                                </p>
                              </div>
                            </td>
                            <td>
                              <small>{new Date(report.created_at).toLocaleDateString()}</small>
                            </td>
                            <td>
                              <span className={`badge ${getStatusColor(report.status)}`}>
                                {report.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeReporterModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default FlaggedUsers;
